import {
	Button,
	Col,
	DatePicker,
	Form,
	Input,
	Modal,
	Row,
	Select,
	message,
} from 'antd';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import get from 'lodash/get';
import { useCallback, useEffect, useRef, useState } from 'react';
import { queryContactsByUserIdIndexMinimal } from 'src/_shared/api/graphql/custom';
import EmployeePicker from 'src/RnRCommonComponent/EmployeePicker/EmployeePickerComponent.jsx';
import getSymbolFromCurrency from 'currency-symbol-map';
import { currencies } from 'src/settings-dashboard/referral-tiers/commonCurrency.js';
import {
	ml,
	sanitize,
	searchOpenSearchNetwork,
} from '../../_shared/services/utils.js';

function AddBonusModal({
	open,
	handleClose,
	onCreateBonus,
	fetchInitialBonuses,
	currentUser,
	allMultiLingualData,
	client,
}) {
	const [form] = Form.useForm();
	const [contacts, setContacts] = useState(null);
	const [recipientType, setRecipientType] = useState(null);
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
	const [selectedContactId, setSelectedContactId] = useState(null);
	const [selectedJobId, setSelectedJobId] = useState(null);
	const [bonusDate, setBonusDate] = useState('');
	const [currency, setCurrency] = useState('USD');
	const [searchedEmployees, setSearchedEmployees] = useState([]);
	const [jobOptions, setJobOptions] = useState([]);
	const [jobSearchLoading, setJobSearchLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const jobSearchTimerRef = useRef(null);

	const handleJobSearch = useCallback(
		(value) => {
			if (jobSearchTimerRef.current) {
				clearTimeout(jobSearchTimerRef.current);
			}

			if (!value) {
				setJobOptions([]);
				return;
			}

			setJobSearchLoading(true);

			jobSearchTimerRef.current = setTimeout(async () => {
				try {
					const response = await searchOpenSearchNetwork(
						{
							query: value,
							size: 300,
							role: currentUser?.role,
							currentUser: currentUser?.id,
							filters: {
								bonuses: [],
								companies: [currentUser?.companyId],
								departments: [],
								distance: '0',
								subCompanies: [],
								jobLevels: [],
							},
						},
						'erin-jobs'
					);

					const options = (response?.data || []).map((job) => ({
						value: job.id,
						label:
							job.title +
							(job.externalJobId ? ` - ID#: ${job.externalJobId}` : ''),
					}));

					setJobOptions(options);
				} catch (error) {
					console.error(error);
					setJobOptions([]);
				} finally {
					setJobSearchLoading(false);
				}
			}, 300);
		},
		[currentUser]
	);

	useEffect(() => {
		return () => {
			if (jobSearchTimerRef.current) {
				clearTimeout(jobSearchTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!selectedEmployeeId) return;

		const employee = searchedEmployees.find((e) => e.id === selectedEmployeeId);
		const employeeCurrency = employee?.userGroup?.currency || 'USD';
		setCurrency(employeeCurrency);
		form.setFieldsValue({ currency: employeeCurrency });

		let cancelled = false;

		async function fetchContacts() {
			try {
				const allContacts = [];

				const getContacts = async (next = null) => {
					const { data } = await client.query({
						query: gql(queryContactsByUserIdIndexMinimal),
						variables: {
							userId: selectedEmployeeId,
							nextToken: next,
						},
						fetchPolicy: 'network-only',
					});
					const result = data.queryContactsByUserIdIndex.items;
					allContacts.push(...result);
					const { nextToken } = data.queryContactsByUserIdIndex;
					if (nextToken) {
						await getContacts(nextToken);
					}
				};

				await getContacts();

				if (!cancelled) {
					setContacts(allContacts);
				}
			} catch (error) {
				console.error(error);
			}
		}

		fetchContacts();

		return () => {
			cancelled = true;
		};
	}, [selectedEmployeeId, client]);

	const handleClose_ = () => {
		setContacts(null);
		setRecipientType(null);
		setSelectedEmployeeId(null);
		setSelectedContactId(null);
		setSelectedJobId(null);
		setBonusDate('');
		setCurrency('USD');
		setSearchedEmployees([]);
		setJobOptions([]);
		setJobSearchLoading(false);
		setLoading(false);
		form.resetFields();
		handleClose();
	};

	const addBonus = async (values) => {
		const selectedDate = dayjs(bonusDate).toISOString();
		setLoading(true);

		let amountDue = get(values, 'amountInput', 0);
		amountDue &&= sanitize(amountDue);

		const input = {
			input: {
				companyId: currentUser?.companyId,
				contactId: selectedContactId,
				userId: selectedEmployeeId,
				jobId: selectedJobId,
				bonusStatus: 'pending',
				recipientType,
				amountDue,
				currency,
				hireDate: selectedDate,
				startDate: selectedDate,
				earnedDate: selectedDate,
				payment: '1 of 1',
				bonusType: 'Employee Referral',
				creationType: 'manual',
			},
		};

		try {
			await onCreateBonus(input);
			message.success('Bonus is added successfully.', 5);
			// Delay refetch to allow OpenSearch to index the new record
			setTimeout(() => fetchInitialBonuses(), 1000);
			handleClose_();
		} catch (error) {
			console.error(error);
			message.error('Failed to create bonus. Please try again.', 5);
			setLoading(false);
		}
	};

	const renderContact = () => {
		const selectedOption = contacts?.map((item) => (
			<Select.Option key={item?.id} value={item?.id}>
				{item?.firstName + ' ' + item?.lastName}
			</Select.Option>
		));

		return (
			<Form.Item
				name="candidateId"
				rules={[{ required: true, message: 'Please select a candidate' }]}
			>
				<Select
					showSearch
					className="custom-input"
					placeholder={
						contacts?.length === 0
							? 'No Candidates Found'
							: 'Type a Candidate Name'
					}
					optionFilterProp="children"
					filterOption={(input, option) =>
						option.props.children.toLowerCase().includes(input.toLowerCase())
					}
					disabled={!contacts || contacts.length === 0}
					onChange={(id) => setSelectedContactId(id)}
				>
					{selectedOption}
				</Select>
			</Form.Item>
		);
	};

	const renderJobs = () => {
		return (
			<Form.Item
				name="jobId"
				rules={[{ required: true, message: 'Please select a job' }]}
			>
				<Select
					showSearch
					className="custom-input"
					placeholder="Search by Title, Req Id, etc."
					filterOption={false}
					loading={jobSearchLoading}
					notFoundContent={jobSearchLoading ? 'Searching...' : null}
					options={jobOptions}
					onSearch={handleJobSearch}
					onChange={(id) => setSelectedJobId(id)}
				/>
			</Form.Item>
		);
	};

	return (
		<Modal
			centered
			open={open}
			footer={null}
			title="Create a Bonus"
			onCancel={handleClose_}
		>
			<Form form={form} onFinish={addBonus}>
				<Row gutter={32}>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Employee', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							<Form.Item
								name="employeeId"
								rules={[
									{ required: true, message: 'Please select an employee' },
								]}
							>
								<EmployeePicker
									selectedEmployee={selectedEmployeeId}
									setSelectedEmployee={(id) => setSelectedEmployeeId(id)}
									setSearchedEmployeeData={setSearchedEmployees}
									className="custom-input bonus-employee-picker"
									style={{}}
									form={form}
									fieldName="employeeId"
									clearOnFocus
								/>
							</Form.Item>
						</div>
					</Col>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Candidate', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							{renderContact()}
						</div>
					</Col>
				</Row>
				<Row gutter={32}>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Recipient', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							<Form.Item
								name="recipientType"
								rules={[
									{
										required: true,
										message: 'Please select a Recipient Type.',
									},
								]}
								validateTrigger={['onChange', 'onBlur']}
							>
								<Select
									className="custom-input"
									onChange={(value) => setRecipientType(value)}
								>
									<Select.Option key={1} value="employee">
										Employee
									</Select.Option>
									<Select.Option key={2} value="candidate">
										Candidate
									</Select.Option>
								</Select>
							</Form.Item>
						</div>
					</Col>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Job', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							{renderJobs()}
						</div>
					</Col>
				</Row>
				<Row gutter={32}>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Amount', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							<Form.Item
								name="amountInput"
								rules={[{ required: true, message: 'Please enter Amount.' }]}
							>
								<Input className="custom-input" placeholder="Amount" />
							</Form.Item>
						</div>
					</Col>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Currency', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							<Form.Item
								name="currency"
								initialValue="USD"
								rules={[
									{ required: true, message: 'Please select a currency.' },
								]}
							>
								<Select
									showSearch
									className="custom-input"
									optionFilterProp="children"
									onChange={(value) => setCurrency(value)}
								>
									{Object.keys(currencies).map((key) => (
										<Select.Option key={key} value={key}>
											{getSymbolFromCurrency(key)} {key}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</div>
					</Col>
				</Row>
				<Row gutter={32}>
					<Col sm={12} xs={24}>
						<div className="custom-form-group">
							<label className="custom-label">
								{ml('Date', currentUser, allMultiLingualData)}:&nbsp;
							</label>
							<Form.Item
								name="date"
								rules={[{ required: true, message: 'Please select date' }]}
							>
								<DatePicker
									disabledDate={(current) => {
										const customDate = dayjs().format('YYYY-MM-DD');
										return current && current < dayjs(customDate, 'YYYY-MM-DD');
									}}
									className="custom-input"
									format="MM-DD-YYYY"
									onChange={(value) => setBonusDate(value)}
								/>
							</Form.Item>
						</div>
					</Col>
				</Row>
				<div className="modal-footer-btn">
					<Button
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={loading}
					>
						{ml('Add Bonus', currentUser, allMultiLingualData)}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}

export default AddBonusModal;
